import { Head } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import {
  type BookingCompletion,
  type BookingPrefill,
} from "@/components/widget/booking-calendar-card"
import {
  type CalendarMessage,
  WebsiteChatWidgetView,
  type WidgetMessage,
} from "@/components/widget/website-chat-widget-view"
import { consumer } from "@/lib/cable"
import {
  websiteChatWidgetBookingPath,
  websiteChatWidgetClosePath,
  websiteChatWidgetMessagesPath,
  websiteChatWidgetSessionPath,
} from "@/routes"

interface WidgetConfig {
  uid: string
  bot_name: string
  primary_color: string
  bubble_position: "bottom-right" | "bottom-left"
  welcome_message: string
  popup_enabled: boolean
  conversation_starters: string[]
  embed_domain: string | null
  turnstile_site_key: string | null
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string
          callback: (token: string) => void
          "error-callback"?: () => void
        },
      ) => string
    }
  }
}

// Defense-in-depth alongside the frame-ancestors CSP: only run when framed by
// the declared domain. `ancestorOrigins` is browser-populated (not settable by
// page JS); when it's unavailable (e.g. Firefox) we defer to the CSP, which
// that browser also enforces.
function framedByAllowedDomain(domain: string | null): boolean {
  if (typeof window === "undefined") return true
  if (window.top === window.self) return false // not embedded at all

  const ancestors = window.location.ancestorOrigins
  if (!ancestors || ancestors.length === 0) return true // can't check → trust CSP

  // IMP-530: our own app framing the widget (the Manage drawer's Test tab) is
  // always allowed — it mirrors the 'self' the frame-ancestors CSP grants.
  if (ancestors[0] === window.location.origin) return true

  if (!domain) return false

  let host: string
  try {
    host = new URL(ancestors[0]).hostname.replace(/^www\./i, "")
  } catch {
    return false
  }
  const d = domain.replace(/^www\./i, "")
  return host === d || host.endsWith(`.${d}`)
}

// Lazily load Cloudflare Turnstile and solve one (managed) challenge, resolving
// the token. Memoized — one token per page load is enough (the session opens
// once). Resolves null if the script is blocked or errors; the server decides
// whether a missing token is fatal (only when Turnstile is enabled there).
let turnstilePromise: Promise<string | null> | null = null
function getTurnstileToken(siteKey: string): Promise<string | null> {
  if (turnstilePromise) return turnstilePromise

  turnstilePromise = new Promise<string | null>((resolve) => {
    function render() {
      if (!window.turnstile) return resolve(null)
      const el = document.createElement("div")
      el.style.display = "none"
      document.body.appendChild(el)
      window.turnstile.render(el, {
        sitekey: siteKey,
        callback: (token) => resolve(token),
        "error-callback": () => resolve(null),
      })
    }
    if (window.turnstile) return render()
    const s = document.createElement("script")
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
    s.async = true
    s.onload = render
    s.onerror = () => resolve(null)
    document.head.appendChild(s)
  })
  return turnstilePromise
}

// The message shapes live with the shared widget view; the calendar card is
// serializable so it survives sessionStorage restore like any other message.
type Message = WidgetMessage

interface Stored {
  token: string | null
  messages: Message[]
}

const ERROR_TEXT = "Sorry — something went wrong. Please try again."

function storageKey(uid: string) {
  return `imp-wc-${uid}`
}

function loadStored(uid: string): Stored {
  if (typeof window !== "undefined") {
    try {
      const raw = window.sessionStorage.getItem(storageKey(uid))
      if (raw) {
        const parsed = JSON.parse(raw) as Stored
        if (Array.isArray(parsed.messages) && parsed.messages.length > 0) {
          return parsed
        }
      }
    } catch {
      // ignore corrupt/unavailable storage
    }
  }
  return { token: null, messages: [] }
}

export default function WebsiteChatWidget({ agent }: { agent: WidgetConfig }) {
  const initial = loadStored(agent.uid)

  // Computed once: refuse to run unless framed by the declared domain.
  const [allowed] = useState(() => framedByAllowedDomain(agent.embed_domain))
  const [open, setOpen] = useState(false)
  const [token, setToken] = useState<string | null>(initial.token)
  const [messages, setMessages] = useState<Message[]>(initial.messages)
  const [input, setInput] = useState("")
  const [sending, setSending] = useState(false)
  // IMP-422: a one-time proactive greeting bubble shown beside the launcher
  // before the visitor first opens the chat.
  const [teaser, setTeaser] = useState(false)
  // IMP-454: on a fresh conversation the welcome message opens the thread as
  // the first bot message. Decided once per page load — it stays up while the
  // visitor chats, but a resumed transcript doesn't repeat the greeting. It's
  // display only: never persisted, never part of the model's context.
  const [greeting] = useState(initial.messages.length === 0)

  // The launcher has been opened at least once — suppresses the greeting teaser.
  const hasOpenedRef = useRef(false)
  // Guards against opening more than one Retell session per visitor session.
  const sessionPromise = useRef<Promise<string | null> | null>(null)
  // Latest token, read by the pagehide beacon without re-registering the listener.
  const tokenRef = useRef<string | null>(initial.token)
  useEffect(() => {
    tokenRef.current = token
  }, [token])

  // The widget lives in an iframe sized by the loader; tell the parent to grow/
  // shrink it as the window opens/closes, and to claim a larger closed footprint
  // while the greeting teaser is showing (otherwise the launcher-sized frame
  // clips it).
  useEffect(() => {
    window.parent.postMessage(
      { imperativeChat: true, type: "resize", open, teaser: teaser && !open },
      "*",
    )
  }, [open, teaser])

  // Make the iframe document transparent so only the launcher/window show.
  // IMP-395 follow-up: the app-wide appearance bootstrap (initializeTheme)
  // stamps `color-scheme: dark` + the .dark class on this document under
  // system dark mode — and a color-scheme mismatch with the host iframe makes
  // the browser paint the frame as an opaque dark box. The widget UI is
  // light-styled; pin the document to a normal scheme, and keep it pinned
  // when the system theme changes (the bootstrap listens for that too).
  useEffect(() => {
    function pinTransparent() {
      document.documentElement.classList.remove("dark")
      document.documentElement.style.colorScheme = "normal"
      document.documentElement.style.background = "transparent"
      document.body.style.background = "transparent"
    }
    pinTransparent()
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    media.addEventListener("change", pinTransparent)
    return () => media.removeEventListener("change", pinTransparent)
  }, [])

  // Persist the running conversation for resume within the visitor's session.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        storageKey(agent.uid),
        JSON.stringify({ token, messages }),
      )
    } catch {
      // storage full/blocked — chat still works in-memory
    }
  }, [agent.uid, token, messages])

  // On open, retire the greeting teaser and remember we've been opened. The
  // composer focus, transcript scroll position, and follow-the-live-edge
  // behavior are owned by the shared widget view.
  useEffect(() => {
    if (!open) return
    hasOpenedRef.current = true
    setTeaser(false)
  }, [open])

  // IMP-422: a one-time proactive greeting. If the visitor hasn't opened the
  // chat on a fresh (unstarted) conversation, nudge them a moment after load with
  // the welcome message beside the launcher. The loader grows the closed iframe
  // to fit it (see the resize effect above). Runs once on mount.
  // IMP-454: the popup is a per-agent setting now — with it off, never nudge.
  useEffect(() => {
    if (!agent.popup_enabled || hasOpenedRef.current || messages.length > 0)
      return
    const id = window.setTimeout(() => {
      if (!hasOpenedRef.current) setTeaser(true)
    }, 1400)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the visitor leaves the page, tell the server to end the chat so Retell
  // analyzes it (outcome + summary for the dashboard) right away rather than
  // waiting for the idle sweep. sendBeacon survives the unload. pagehide is the
  // reliable cross-browser unload signal.
  useEffect(() => {
    function endChat() {
      const t = tokenRef.current
      if (!t || !navigator.sendBeacon) return
      navigator.sendBeacon(websiteChatWidgetClosePath(t))
    }
    window.addEventListener("pagehide", endChat)
    return () => window.removeEventListener("pagehide", endChat)
  }, [])

  // Subscribe to the conversation's channel once we have a token. The reply to
  // each message is delivered here (produced by WebsiteChatReplyJob).
  useEffect(() => {
    if (!token) return

    const subscription = consumer.subscriptions.create(
      { channel: "WebsiteChatChannel", conversation_uid: token },
      {
        received(data: Record<string, unknown>) {
          switch (data.type) {
            case "reply":
              setSending(false)
              setMessages((prev) => [
                ...prev,
                { role: "bot", text: (data.content as string) || ERROR_TEXT },
              ])
              break
            case "error":
              setSending(false)
              setMessages((prev) => [
                ...prev,
                { role: "bot", text: ERROR_TEXT },
              ])
              break
            // IMP-395: the agent reached its booking step — show the provider's
            // calendar as a card. One open card at a time (re-broadcasts from
            // repeated availability checks are the recovery path for a missed
            // event, not a reason to stack cards); a fresh card after a booked
            // one supports a second appointment.
            case "booking_calendar":
              setMessages((prev) => {
                if (
                  prev.some((m) => m.role === "calendar" && m.status === "open")
                ) {
                  return prev
                }
                return [
                  ...prev,
                  {
                    role: "calendar",
                    provider: data.provider as CalendarMessage["provider"],
                    url: data.url as string,
                    prefill: (data.prefill ?? {}) as BookingPrefill,
                    status: "open",
                  },
                ]
              })
              break
          }
        },
      },
    )

    return () => subscription.unsubscribe()
  }, [token])

  // IMP-395: the visitor completed a booking inside the embedded calendar.
  // Report it (recorded like a conversational booking server-side; the agent
  // acknowledges via a normal reply), flip the card, confirm in-chat.
  function handleBooked(index: number, completion: BookingCompletion) {
    setMessages((prev) =>
      prev.map((m, i) =>
        i === index && m.role === "calendar"
          ? { ...m, status: "booked" as const }
          : m,
      ),
    )
    const t = tokenRef.current
    if (t) {
      void fetch(websiteChatWidgetBookingPath(t), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(completion),
      }).catch(() => {
        // The booking itself already succeeded on the provider; recording is
        // best-effort from the widget's perspective.
      })
    }
  }

  // Open (or reuse) the Retell-backed conversation; returns the token.
  async function ensureSession(): Promise<string | null> {
    if (token) return token
    if (sessionPromise.current) return sessionPromise.current

    sessionPromise.current = (async () => {
      try {
        const turnstileToken = agent.turnstile_site_key
          ? await getTurnstileToken(agent.turnstile_site_key)
          : null
        const res = await fetch(websiteChatWidgetSessionPath(agent.uid), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ turnstile_token: turnstileToken }),
        })
        if (!res.ok) return null
        const data = (await res.json()) as { token?: string }
        if (data.token) {
          setToken(data.token)
          return data.token
        }
      } catch {
        // fall through
      }
      return null
    })()

    return sessionPromise.current
  }

  async function send(text?: string) {
    const trimmed = (text ?? input).trim()
    if (!trimmed || sending) return

    setMessages((prev) => [...prev, { role: "visitor", text: trimmed }])
    setInput("")
    setSending(true)

    const convoToken = await ensureSession()
    if (!convoToken) {
      setSending(false)
      setMessages((prev) => [...prev, { role: "bot", text: ERROR_TEXT }])
      return
    }

    try {
      const res = await fetch(websiteChatWidgetMessagesPath(convoToken), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ content: trimmed }),
      })
      // The bot's reply arrives over the channel; a non-2xx means it never will.
      if (!res.ok) throw new Error("send failed")
      // IMP-395 follow-up: if the Retell session behind the conversation had
      // ended, the server renews it and echoes a fresh token — adopt it (the
      // channel subscription follows via the token effect; visible history is
      // untouched).
      const data = (await res.json().catch(() => null)) as {
        token?: string
      } | null
      if (data?.token && data.token !== convoToken) {
        setToken(data.token)
      }
    } catch {
      setSending(false)
      setMessages((prev) => [...prev, { role: "bot", text: ERROR_TEXT }])
    }
  }

  // Not embedded on the declared domain — render nothing (the frame-ancestors
  // CSP already blocks framing elsewhere; this also covers direct/top-level loads).
  if (!allowed) return <Head title={agent.bot_name} />

  return (
    <>
      <Head title={agent.bot_name} />
      <WebsiteChatWidgetView
        config={agent}
        messages={messages}
        input={input}
        sending={sending}
        open={open}
        teaser={teaser}
        greeting={greeting}
        onOpenChange={setOpen}
        onInputChange={setInput}
        onSend={(text) => void send(text)}
        onDismissTeaser={() => setTeaser(false)}
        onBooked={handleBooked}
      />
    </>
  )
}
